import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Dropdown } from 'react-bootstrap';

interface PortalDropdownProps {
  children: React.ReactNode;
  className?: string;
  show?: boolean;
  onToggle?: (isOpen: boolean) => void;
  [key: string]: any; // Allow other props to pass through
}

/**
 * PortalDropdown - A dropdown component that renders its menu in a portal
 * This solves the issue of dropdowns being clipped by parent containers with overflow:hidden
 * (like modals)
 */
const PortalDropdown: React.FC<PortalDropdownProps> = ({ 
  children, 
  className, 
  show: controlledShow, 
  onToggle, 
  ...props 
}) => {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const toggleRef = useRef<HTMLDivElement>(null);
  
  // Use controlled show prop
  const isOpen = controlledShow !== undefined ? controlledShow : false;

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Extract Toggle and Menu from children
  const childArray = React.Children.toArray(children);
  const toggle = childArray.find((child: any) => child?.type === Dropdown.Toggle);
  const menu = childArray.find((child: any) => child?.type === Dropdown.Menu);
  const otherChildren = childArray.filter(
    (child: any) => child?.type !== Dropdown.Toggle && child?.type !== Dropdown.Menu
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && toggleRef.current && !toggleRef.current.contains(event.target as Node)) {
        // Check if click is inside the portal menu
        const portalMenu = document.getElementById('portal-dropdown-menu');
        if (portalMenu && !portalMenu.contains(event.target as Node)) {
          if (onToggle) {
            onToggle(false);
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  return (
    <>
      <Dropdown className={className} show={isOpen} onToggle={onToggle} {...props}>
        <div ref={toggleRef}>
          {toggle}
        </div>
        {otherChildren}
        {/* Render a dummy menu to prevent React Bootstrap warnings */}
        {!isOpen && <Dropdown.Menu style={{ display: 'none' }} />}
      </Dropdown>

      {/* Render menu in portal */}
      {isOpen && menu && ReactDOM.createPortal(
        // Wrap in same class as parent to apply the same styles
        <div className={className}>
          <div
            id="portal-dropdown-menu"
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              zIndex: 9999, // High z-index to appear above modals
              display: 'block',
              margin: 0,
              borderRadius: '0.25rem',
              overflow: 'hidden',
            }}
          >
            {/* Render the menu's children directly */}
            {(menu as any).props.children}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PortalDropdown;